import { ContextMenuAction } from 'components/mrp/components/ContextMenu/ContextMenu.types';
import { MrpShortcuts } from 'components/mrp/mrp.consts';
import { useCallback, useContext, useMemo } from 'react';
import {
  AvailabilityEventData,
  ClickData,
  ClickTargetType,
  TimeSlotData
} from './EntityCalendarRightClickHandler.types';
import { CalendarInteractionContext } from '../CalendarInteractionProvider/CalendarInteractionProvider.context';
import { useCalendarInteractionStore } from '../store/calendarInteractionStore.store';
import dayjs from 'dayjs';
import { PlanningEntityType } from 'generated/index';
import i18n from 'config/i18n';
import { getLocale } from 'utils/date';
import { mutations } from 'RestAPIs/rapini';
import { useEntityListener } from 'components/mrp/mrp.hooks';
import { useCalendarStore } from 'components/mrp/stores/calendarStore/calendarStore';
import { useShallow } from 'zustand/shallow';
import { useSelector } from 'react-redux';

const t = i18n.getFixedT(getLocale(), 'common', 'mrp');

const extractTargetFromClickEvent = (
  event: React.MouseEvent<HTMLDivElement>
): {
  target?: Element;
  clickTargetType?: ClickTargetType;
} => {
  const empty = {
    target: undefined,
    clickTargetType: undefined
  };
  const clickTarget = event.target as HTMLDivElement;

  const isTimeSlotCell = clickTarget.classList.contains('time-slot-cell');
  const isAvailabilityEvent =
    clickTarget.classList.contains('availability-event');
  if (!isTimeSlotCell && !isAvailabilityEvent) {
    return empty;
  }

  const target = isTimeSlotCell
    ? clickTarget.closest('.time-slot-cell-container')
    : clickTarget;

  if (!target) return empty;

  return {
    target,
    clickTargetType: isTimeSlotCell ? 'TIMESLOT' : 'AVAILABILITY_EVENT'
  };
};

export const getCellDataFromClickEvent = (
  event: React.MouseEvent<HTMLDivElement>
): ClickData | undefined => {
  const { target, clickTargetType } = extractTargetFromClickEvent(event);
  if (!target) return;

  event.preventDefault();
  event.stopPropagation();

  const timeStamp = target.getAttribute('data-time-stamp');
  const date = !!timeStamp ? dayjs(timeStamp) : undefined;
  const calendarId = target.getAttribute('data-calendar-id') || undefined;
  const entityId = target.getAttribute('data-entity-id') || undefined;
  const sourceType =
    (target.getAttribute('data-source-type') as PlanningEntityType) ||
    undefined;

  return {
    calendarId,
    date,
    entityId,
    clickTargetType,
    sourceType
  };
};

const useGetTimeSlotRightClickActions = (
  closeAction: () => void,
  timeSlotData?: TimeSlotData
) => {
  const sourceEvent = useCalendarInteractionStore((state) => state.sourceEvent);
  const hasAddAppointmentAccess = useSelector(
    ({ grants }) => grants.hasAddAppointmentAccess
  );

  const { pasteEvent: pasteEventUtil, addAppointment: addAppointmentUtil } =
    useContext(CalendarInteractionContext);

  const pasteEvent = useCallback(() => {
    if (!timeSlotData || !timeSlotData.calendarId || !timeSlotData.date) return;

    pasteEventUtil(timeSlotData.calendarId, timeSlotData.date);
  }, [pasteEventUtil, timeSlotData]);

  const addAppointment = useCallback(() => {
    if (!timeSlotData) return;

    addAppointmentUtil(timeSlotData?.calendarId, timeSlotData?.date);
  }, [addAppointmentUtil, timeSlotData]);

  const actions: ContextMenuAction[] = useMemo(() => {
    const menuActions: ContextMenuAction[] = [];

    if (hasAddAppointmentAccess) {
      menuActions.push({
        value: t('addAppointment'),
        icon: {
          iconType: 'calendar-plus'
        },
        onClick: () => {
          addAppointment();
          closeAction();
        },
        shortcutHint: MrpShortcuts.NEW_APPOINTMENT
      });
    }

    if (!!sourceEvent && hasAddAppointmentAccess) {
      menuActions.push({
        value: t('pasteAppointment'),
        icon: {
          iconType: 'paste'
        },
        onClick: () => {
          pasteEvent();
          closeAction();
        },
        shortcutHint: MrpShortcuts.PASTE_APPOINTMENT
      });
    }

    return menuActions;
  }, [
    hasAddAppointmentAccess,
    sourceEvent,
    addAppointment,
    closeAction,
    pasteEvent
  ]);

  return {
    actions
  };
};

const useGetAvailabilityEventRightClickActions = (
  closeAction: () => void,
  availabilityEventData?: AvailabilityEventData
) => {
  const { mutateAsync } = mutations.useDeleteAvailabilityParticipants();

  const calendars = useCalendarStore(useShallow((state) => state.calendars));

  const { refetchByPlanningEntityIdentifiers } = useEntityListener({
    listen: false,
    planningEntityIdentifiers: []
  });

  const deleteAvailability = useCallback(async () => {
    if (!availabilityEventData) return;

    const targetEntities = calendars[
      availabilityEventData.calendarId!
    ].sources.map(({ planningEntityIdentifier }) => planningEntityIdentifier);

    await mutateAsync({
      id: availabilityEventData?.entityId?.toString() || ''
    });

    closeAction();
    refetchByPlanningEntityIdentifiers(targetEntities);
  }, [
    availabilityEventData,
    calendars,
    mutateAsync,
    closeAction,
    refetchByPlanningEntityIdentifiers
  ]);

  const actions: ContextMenuAction[] = [
    {
      value: t('deleteAvailability'),
      icon: {
        iconType: 'trash-can'
      },
      onClick: deleteAvailability,
      disabled:
        availabilityEventData?.sourceType == 'EMPLOYEE' ||
        availabilityEventData?.sourceType === 'EMPLOYMENT'
    }
  ];

  return {
    actions
  };
};

export const useGetEntityRightClickActions = (
  closeAction: () => void,
  clickData?: ClickData
) => {
  const { actions: timeSlotActions } = useGetTimeSlotRightClickActions(
    closeAction,
    clickData
  );

  const { actions: availabilityEventActions } =
    useGetAvailabilityEventRightClickActions(closeAction, clickData);

  if (!clickData || !clickData.clickTargetType) return [];

  switch (clickData.clickTargetType) {
    case 'TIMESLOT':
      return timeSlotActions;
    case 'AVAILABILITY_EVENT':
      return availabilityEventActions;
  }
};
